import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useRowEditor } from "../context/QuoteEditorContext"
import IconButton from "@react/shared/ui/IconButton"
import IconBadge  from "@react/shared/ui/IconBadge"
import Button     from "@react/shared/ui/Button"
import Modal      from "@react/shared/ui/Modal"
import { useFitInViewport } from "@react/shared/ui/DropdownMenu"
import { countBy, isOption } from "../helpers/treeMath"
import { plainText } from "../helpers/formatting"
import { COST_CATEGORIES, costCategoryVisual } from "@react/quotes/qline_service_blueprints/kinds"

const RowMenu = ({
  node,
  onAddSiblingGroup,
  onAddSubsection,
  onAddGroup,
  onAddLine,
  onAddArticle,
  onAddDescription,
  onLinkProduct,
  onUnlinkProduct,
  onAddDiscount,
  onRemoveDiscount,
  subLabel = I18n.t("quotes.builder.row_menu.default_sublabel"),
}) => {
  const { settings, dispatch } = useRowEditor()
  const [open, setOpen] = useState(false)
  const [pos,  setPos]  = useState({ top: 0, left: 0 })
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const [submenuPos,  setSubmenuPos]  = useState({ top: 0, left: 0 })
  const [confirming,  setConfirming]  = useState(false)
  const btnRef             = useRef(null)
  const menuRef            = useRef(null)
  const submenuTriggerRef  = useRef(null)
  const submenuRef         = useRef(null)
  const submenuCloseTimer  = useRef(null)
  const menuPlaced         = useFitInViewport(menuRef, pos)
  const submenuPlaced      = useFitInViewport(submenuRef, submenuPos)

  useEffect(() => () => { if (submenuCloseTimer.current) clearTimeout(submenuCloseTimer.current) }, [])
  useEffect(() => { if (!open) setSubmenuOpen(false) }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (menuRef.current?.contains(e.target)) return
      if (submenuRef.current?.contains(e.target)) return
      if (btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey    = e => { if (e.key === "Escape") setOpen(false) }
    const onScroll = () => setOpen(false)
    const onOtherOpen = e => { if (e.detail?.owner !== btnRef.current) setOpen(false) }
    document.addEventListener("click", onClick, true)
    document.addEventListener("keydown", onKey)
    window.addEventListener("scroll", onScroll, true)
    document.addEventListener("rowmenu:open", onOtherOpen)
    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("scroll", onScroll, true)
      document.removeEventListener("rowmenu:open", onOtherOpen)
    }
  }, [open])

  const toggle = e => {
    e.stopPropagation()
    if (open) { setOpen(false); return }
    const rect = btnRef.current.getBoundingClientRect()
    const menuWidth = 220
    let left = rect.right - menuWidth
    if (left < 8) left = 8
    setPos({ top: rect.bottom + 4, left, flipBottom: rect.top - 4 })
    setOpen(true)
    document.dispatchEvent(new CustomEvent("rowmenu:open", { detail: { owner: btnRef.current } }))
  }

  const run = handler => e => {
    e.stopPropagation()
    setOpen(false)
    handler?.()
  }

  const dispatchAction = type => () => {
    if (!node?._uid) return
    dispatch({ type, payload: { nodeUid: node._uid } })
  }

  // What a deletion would take away: the priced lines under this node. A single
  // line, or a still empty section, goes without asking anything — undo takes
  // it back.
  const doomedLines = node && node.type !== "service" ? countBy(node, "service") : 0
  const needsConfirm = doomedLines > 0

  const deleteNow = () => {
    setConfirming(false)
    dispatch({ type: "DELETE_NODE", payload: { nodeUid: node._uid } })
  }

  const askDelete = () => {
    if (needsConfirm) setConfirming(true)
    else deleteNow()
  }

  const SUBMENU_WIDTH = 200
  const openSubmenu = () => {
    if (submenuCloseTimer.current) { clearTimeout(submenuCloseTimer.current); submenuCloseTimer.current = null }
    const rect = submenuTriggerRef.current?.getBoundingClientRect()
    if (!rect) return
    let left = rect.right + 2
    if (left + SUBMENU_WIDTH > window.innerWidth - 8) left = rect.left - SUBMENU_WIDTH - 2
    if (left < 8) left = 8
    setSubmenuPos({ top: rect.top, left })
    setSubmenuOpen(true)
  }
  const scheduleCloseSubmenu = () => {
    if (submenuCloseTimer.current) clearTimeout(submenuCloseTimer.current)
    submenuCloseTimer.current = setTimeout(() => setSubmenuOpen(false), 120)
  }

  const itemBase   = "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-[13px] font-medium text-left [&_svg]:w-3.5"
  const item         = `${itemBase} text-slate-700 hover:bg-slate-100 hover:text-slate-900 [&_svg]:text-slate-400 hover:[&_svg]:text-slate-700`
  const itemCategory = "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-[13px] font-medium text-left text-slate-700 hover:bg-slate-100 hover:text-slate-900"
  const itemDanger   = `${itemBase} text-rose-600 hover:bg-rose-50 [&_svg]:text-rose-600`

  const enabledCategories = settings.account?.enabled_cost_categories
  const allowedCategoryKeys = (Array.isArray(enabledCategories) && enabledCategories.length > 0)
    ? enabledCategories
    : COST_CATEGORIES.map(category => category.value)
  const lineCategories = COST_CATEGORIES.filter(category => allowedCategoryKeys.includes(category.value))

  const hasAddActions  = !!(onAddSiblingGroup || onAddSubsection || onAddGroup || onAddLine || onAddArticle || onAddDescription)
  const hasLinkActions = !!(onLinkProduct || onUnlinkProduct || onAddDiscount || onRemoveDiscount)

  return (
    <>
      <IconButton
        ref       = {btnRef}
        size      = "sm"
        title     = {I18n.t("quotes.builder.row_menu.actions")}
        onClick   = {toggle}
        className = {open ? "bg-slate-100 text-slate-900" : ""}
      >
        <FontAwesomeIcon icon="ellipsis-v" />
      </IconButton>

      {open && createPortal(
        <div
          ref       = {menuRef}
          style     = {{ top: `${menuPlaced.top}px`, left: `${menuPlaced.left}px` }}
          className = "fixed min-w-[220px] bg-white border border-slate-200 rounded-[10px] p-1.5 z-[1200] font-sans text-[13px]"
        >
          {onAddSiblingGroup && (
            <button type="button" className={item} onClick={run(onAddSiblingGroup)}>
              <FontAwesomeIcon icon="layer-group" /> {I18n.t("quotes.builder.row_menu.add_sibling_group")}
            </button>
          )}
          {onAddSubsection && (
            <button type="button" className={item} onClick={run(onAddSubsection)}>
              <FontAwesomeIcon icon="folder-tree" /> {I18n.t("quotes.builder.row_menu.add_subsection", { sublabel: subLabel })}
            </button>
          )}
          {onAddGroup && (
            <button type="button" className={item} onClick={run(onAddGroup)}>
              <FontAwesomeIcon icon="cubes" /> {I18n.t("quotes.builder.row_menu.add_group_from_library")}
            </button>
          )}
          {onAddLine && (
            <div
              className    = "relative"
              onMouseEnter = {openSubmenu}
              onMouseLeave = {scheduleCloseSubmenu}
            >
              <button
                ref       = {submenuTriggerRef}
                type      = "button"
                className = {`${item} justify-between`}
                onClick   = {e => { e.stopPropagation(); openSubmenu() }}
              >
                <span className="flex items-center gap-2.5">
                  <FontAwesomeIcon icon="plus" /> {I18n.t("quotes.builder.row_menu.add_line")}
                </span>
                <FontAwesomeIcon icon="chevron-right" className="!w-2.5" />
              </button>
            </div>
          )}
          {onAddArticle && (
            <button type="button" className={item} onClick={run(onAddArticle)}>
              <FontAwesomeIcon icon="box" /> {I18n.t("quotes.builder.row_menu.add_article")}
            </button>
          )}
          {onAddDescription && (
            <button type="button" className={item} onClick={run(onAddDescription)}>
              <FontAwesomeIcon icon="align-left" /> {I18n.t("quotes.builder.row_menu.add_description")}
            </button>
          )}
          {hasAddActions && hasLinkActions && <div className="h-px bg-slate-200 mx-0.5 my-1" />}

          {onLinkProduct && !onUnlinkProduct && (
            <button type="button" className={item} onClick={run(onLinkProduct)}>
              <FontAwesomeIcon icon="cube" /> {I18n.t("quotes.builder.row_menu.link_product")}
            </button>
          )}
          {onUnlinkProduct && (
            <button type="button" className={item} onClick={run(onUnlinkProduct)}>
              <FontAwesomeIcon icon="link-slash" /> {I18n.t("quotes.builder.row_menu.unlink_product")}
            </button>
          )}
          {onAddDiscount && (
            <button type="button" className={item} onClick={run(onAddDiscount)}>
              <FontAwesomeIcon icon="percent" /> {I18n.t("quotes.builder.row_menu.add_discount")}
            </button>
          )}
          {onRemoveDiscount && (
            <button type="button" className={item} onClick={run(onRemoveDiscount)}>
              <FontAwesomeIcon icon="percent" /> {I18n.t("quotes.builder.row_menu.remove_discount")}
            </button>
          )}

          {(hasAddActions || hasLinkActions) && <div className="h-px bg-slate-200 mx-0.5 my-1" />}

          {node?.type === "group" && (
            <button
              type      = "button"
              className = {item}
              onClick   = {run(() => dispatch({ type: "SET_OPTION", payload: { nodeUid: node._uid, value: !isOption(node) } }))}
            >
              <FontAwesomeIcon icon={isOption(node) ? "circle-xmark" : "circle-question"} />
              {I18n.t(isOption(node) ? "quotes.builder.row_menu.unset_option" : "quotes.builder.row_menu.set_option")}
            </button>
          )}
          <button type="button" className={item} onClick={run(dispatchAction("DUPLICATE_NODE"))}>
            <FontAwesomeIcon icon="copy" /> {I18n.t("quotes.builder.row_menu.duplicate")}
          </button>
          <button type="button" className={itemDanger} onClick={run(askDelete)}>
            <FontAwesomeIcon icon="trash" /> {I18n.t("quotes.builder.row_menu.delete")}
          </button>
        </div>,
        document.body
      )}

      {open && submenuOpen && createPortal(
        <div
          ref          = {submenuRef}
          style        = {{ top: `${submenuPlaced.top}px`, left: `${submenuPlaced.left}px` }}
          onMouseEnter = {openSubmenu}
          onMouseLeave = {scheduleCloseSubmenu}
          className    = "fixed min-w-[200px] bg-white border border-slate-200 rounded-[10px] p-1.5 z-[1300] font-sans text-[13px]"
        >
          {lineCategories.map(category => {
            const visual = costCategoryVisual(category.value)
            return (
              <button
                key       = {category.value}
                type      = "button"
                className = {itemCategory}
                onClick   = {run(() => onAddLine(category.value))}
              >
                <IconBadge tone={visual.tone} size="xs">
                  <FontAwesomeIcon icon={visual.icon} />
                </IconBadge>
                {category.label}
              </button>
            )
          })}
        </div>,
        document.body
      )}

      <Modal
        isOpen  = {confirming}
        onClose = {() => setConfirming(false)}
        size    = "sm"
        title   = {I18n.t("quotes.builder.row_menu.confirm_delete_title")}
        footer  = {
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
              {I18n.t("button.cancel")}
            </Button>
            <Button variant="danger" size="sm" onClick={deleteNow}>
              <FontAwesomeIcon icon="trash" />
              {I18n.t("quotes.builder.row_menu.confirm_delete_action")}
            </Button>
          </>
        }
      >
        <p className="m-0 text-[13.5px] text-slate-700 leading-relaxed">
          {I18n.t("quotes.builder.row_menu.confirm_delete_body", {
            count: doomedLines,
            label: plainText(node?.description)
                     || I18n.t("quotes.builder.row_menu.confirm_delete_untitled"),
          })}
        </p>
        <p className="m-0 text-[12.5px] text-slate-500">
          {I18n.t("quotes.builder.row_menu.confirm_delete_undo_hint")}
        </p>
      </Modal>
    </>
  )
}

export default RowMenu
